'use client';

import { useMenuContext } from 'app/[lang]/_contexts/MenuContext';
import { Scalars, TmuData } from 'gql/graphql';
import { toaster } from 'helpers/toaster';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { useTMUData } from 'providers/TranslationMemoryProviders/useTMUData';
import { useTMUHasTermsLanguagePairs } from 'providers/TranslationMemoryProviders/useTMUHasTermsLanguagePairs';
import { useTranslationMemoryDestroy } from 'providers/TranslationMemoryProviders/useTranslationMemoryDestroy';
import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { Control } from 'react-hook-form';
import { notifyException } from 'services/appsignal';
import { Option } from 'types/Option';
import useSearchParamsTM, { FiltersTranslationMemoryForm } from './useSearchParamsTM';

type TTranslationMemoryContext = {
  deferredSearchQuery?: string;
  searchQuery?: string;
  languagesDescription?: string;
  termEdit: string;
  setTermEdit: Dispatch<SetStateAction<string>>;
  editIndex: number;
  setEditIndex: Dispatch<SetStateAction<number>>;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  data: TmuData | undefined;
  optionsLanguagesTM: Option[];
  refetch: () => Promise<void>;
  isLoading: boolean;
  hasTerms: Scalars['Boolean'];
  isChecking: boolean;
  isSelectDisabled: boolean;
  handleOpenDeleteTmModal: (liveTranslationId: string) => void;
  handleCloseDeleteTmModal: () => void;
  handleDeleteTm: () => void;
  modalDeleteTMOpen: boolean;
  control: Control<FiltersTranslationMemoryForm>;
};

const initialState = {
  deferredSearchQuery: '',
  searchQuery: '',
  languagesDescription: '',
  termEdit: '',
  setTermEdit: () => {},
  editIndex: -1,
  setEditIndex: () => {},
  page: 1,
  setPage: () => {},
  data: {} as TmuData,
  refetch: async () => {},
  optionsLanguagesTM: [],
  isLoading: true,
  hasTerms: false,
  isChecking: true,
  isSelectDisabled: false,
  handleOpenDeleteTmModal: () => {},
  handleCloseDeleteTmModal: () => {},
  handleDeleteTm: () => {},
  modalDeleteTMOpen: true,
  control: {} as Control<FiltersTranslationMemoryForm>
};

const TranslationMemoryContext = createContext<TTranslationMemoryContext>(initialState);

const useTranslationMemoryContext = (): TTranslationMemoryContext =>
  useContext(TranslationMemoryContext);

const TranslationMemoryProvider: FC<PropsWithChildren<unknown>> = ({ children }): JSX.Element => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const { handleScrollTop } = useMenuContext();

  const { mutateAsync: translationMemoryDestroy } = useTranslationMemoryDestroy({
    onSuccess: () => {
      handleScrollTop();
      toaster.success(
        t('messages.success.translation_search_delete.paragraph'),
        t('messages.success.translation_search_delete.title')
      );
    },
    onError: (error: unknown) => {
      handleScrollTop();
      notifyException(error);
      toaster.error(
        t('messages.error.translation_search_delete.paragraph'),
        t('messages.error.translation_search_delete.title')
      );
    }
  });

  const [termEdit, setTermEdit] = useState<string>(initialState.termEdit);
  const [editIndex, setEditIndex] = useState<number>(initialState.editIndex);
  const [modalDeleteTMOpen, setModalDeleteTMOpen] = useState(false);
  const [liveTranslationId, setLiveTranslationId] = useState('');

  const handleOpenDeleteTmModal = (liveTranslationId: string) => {
    setModalDeleteTMOpen(true);
    setLiveTranslationId(liveTranslationId);
  };

  const handleCloseDeleteTmModal = () => {
    setLiveTranslationId('');
    setModalDeleteTMOpen(false);
  };

  const handleDeleteTm = async () => {
    await translationMemoryDestroy({
      liveTranslationId
    });
    await refetch();
    setModalDeleteTMOpen(false);
  };

  const {
    control,
    page,
    setPage,
    languagePair,
    deferredSearchQuery,
    searchQuery,
    languagesDescription,
    optionsLanguagesTM
  } = useSearchParamsTM();

  const { data, refetch, isLoading } = useTMUData(
    {
      page,
      query: deferredSearchQuery,
      currentLanguagePair: languagePair
    },
    { enabled: !!languagePair && deferredSearchQuery === searchQuery }
  );

  const {
    data: dataTMUHasTermsLanguagePairs,
    isLoading: isLoadingTMUHasTermsLanguagePairs,
    isPending: isPendingTMUHasTermsLanguagePairs,
    refetch: refetchTMUHasTermsLanguagePairs
  } = useTMUHasTermsLanguagePairs({
    page: 1,
    query: '',
    currentLanguagePair: ''
  });

  const handleRefetch = () => Promise.all([refetch(), refetchTMUHasTermsLanguagePairs()]);

  const hasTerms = !!dataTMUHasTermsLanguagePairs?.data?.hasTerms;
  const isChecking = isLoadingTMUHasTermsLanguagePairs || isPendingTMUHasTermsLanguagePairs;
  const isSelectDisabled = hasTerms === false || !languagePair;

  const onFetch = useCallback(async () => {
    if (!isChecking) {
      if (!!hasTerms && !pathname.includes('/translation-memory/list'))
        return router.push('/translation-memory/list');
      if (!hasTerms) return router.push('/translation-memory');
    }
  }, [hasTerms, isChecking, pathname, router]);

  useEffect(() => {
    onFetch();
  }, [onFetch]);

  return (
    <TranslationMemoryContext.Provider
      value={{
        deferredSearchQuery,
        searchQuery,
        languagesDescription,
        termEdit,
        setTermEdit,
        editIndex,
        setEditIndex,
        page,
        setPage,
        data: data?.data,
        refetch: handleRefetch as unknown as () => Promise<void>,
        optionsLanguagesTM,
        isLoading,
        hasTerms,
        isChecking,
        isSelectDisabled,
        handleOpenDeleteTmModal,
        handleCloseDeleteTmModal,
        handleDeleteTm,
        modalDeleteTMOpen,
        control
      }}
    >
      {children}
    </TranslationMemoryContext.Provider>
  );
};

export { TranslationMemoryProvider, useTranslationMemoryContext };
